import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../../hooks/useAuth';
import { useCart } from '../../../Contexts/CartContext';
import { useCartWithListings } from '../../../hooks/useCartWithListings';
import { useFulfilmentMode } from '../../../hooks/useFulfilmentMode';
import { showToast } from '../../../utils/toast';
import { formatCurrency, formatPrettyDate } from '../../../utils/common';
import { routes } from '../../../routes/routes';
import { CartPageView } from './CartPage.view';

/**
 * Cart, the container. Cart state, localStorage, stock guards, the auth gate
 * and the handoff to `/checkout`.
 *
 * Everything visual lives in `CartPage.view.jsx`, which is pure, so the state
 * explorer can mount the real screen without any of this running. See
 * `design-system/explorer/registry.js` for that convention.
 *
 * EVERY BEHAVIOUR IS PRESERVED, and the ones that matter are:
 * - Quantity and removal mutate the MINIMAL cart (`{ listing_id, quantity }`,
 *   keyed by seller), never the fresh merged `cartData`. `index` is a position
 *   in the fresh array and is resolved to a `listing_id` before the minimal
 *   cart is touched. That indirection is load bearing, not incidental.
 * - The user-scoped localStorage keys are unchanged: `cart_<id>` / `cart_guest`
 *   and `orderItems_<id>` / `orderItems_guest`.
 * - Checkout still validates auth, a non-empty bucket and live stock, in that
 *   order, with the same toast copy, before writing the handoff and navigating.
 * - The handoff shape written to `orderItems_*` is unchanged:
 *   `{ sellerId, items: [{ listing_id, quantity }] }`.
 *
 * TWO THINGS REMOVED, both dead:
 * - `selectedCategory` and its `useMemo` regrouping. The only control that
 *   could set it, `CategorySelect`, was already commented out of `TopBar`, so
 *   the filter could never be anything but "All" and the memo was an identity
 *   function over the whole cart. Its option list was `MjA0NzM5MDIy`,
 *   "Home & Kitchen" and "Sports", on a trading card marketplace.
 * - The `isMobile` state and its `matchMedia("(max-width: 700px)")` listener,
 *   which selected between two complete markup trees. The view is one tree and
 *   the handoff is the standard 1024.
 */

/** Best-effort card identity. Every field here is optional in the payload, so
 *  nothing is rendered unless it actually arrived. */
function readCard(listing) {
  const card = listing?.card || {};
  const cardId = listing?.card_id ?? card.id;
  const setName = card.set?.name || card.set_name || listing?.set_name;
  const number = card.number || card.card_number || listing?.card_number;
  return {
    cardId,
    setName,
    cardNumber: number ? `#${number}` : undefined,
    name: listing?.card_name || card.name || 'Card',
  };
}

export default function CartPage() {
  const fulfilmentMode = useFulfilmentMode();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { cartItems, setCartItems } = useCart();
  const { cartData, isLoading, isError, refetch } = useCartWithListings();

  const getCartKey = () => (user ? `cart_${user.id || user._id}` : 'cart_guest');

  const getSellerDisplayName = (sellerId) => {
    if (!cartData[sellerId] || !cartData[sellerId][0]) return 'Unknown Seller';
    const seller = cartData[sellerId][0].card_listing?.seller;
    if (!seller) return `Seller ${sellerId}`;
    return (
      seller.store_name || seller.full_name || seller.first_name || `Seller ${sellerId}`
    );
  };

  // Mutate the MINIMAL cart (cartItems, keyed by listing_id), never the fresh
  // cartData. `index` refers to the position within the fresh cartData[sellerId]
  // array; resolve it to a listing_id before touching the minimal cart.
  const handleIncrease = (sellerId, index) => {
    const currentItem = cartData[sellerId]?.[index];
    if (!currentItem) return;

    const listingId = currentItem.card_listing.id;
    const availableStock = currentItem.card_listing.quantity;
    const newQuantity = currentItem.quantity + 1;

    if (newQuantity > availableStock) {
      showToast.error(
        `Cannot add more than ${availableStock} items. Only ${availableStock} available in stock.`
      );
      return;
    }

    const updated = { ...cartItems };
    updated[sellerId] = (updated[sellerId] || []).map((it) =>
      it.listing_id === listingId ? { ...it, quantity: newQuantity } : it
    );
    setCartItems(updated);
    localStorage.setItem(getCartKey(), JSON.stringify(updated));
  };

  const handleDecrease = (sellerId, index) => {
    const currentItem = cartData[sellerId]?.[index];
    if (!currentItem || currentItem.quantity <= 1) return;

    const listingId = currentItem.card_listing.id;

    const updated = { ...cartItems };
    updated[sellerId] = (updated[sellerId] || []).map((it) =>
      it.listing_id === listingId ? { ...it, quantity: it.quantity - 1 } : it
    );
    setCartItems(updated);
    localStorage.setItem(getCartKey(), JSON.stringify(updated));
  };

  const handleDelete = (sellerId, index) => {
    const currentItem = cartData[sellerId]?.[index];
    if (!currentItem) return;

    const listingId = currentItem.card_listing.id;

    const updated = { ...cartItems };
    const remaining = (updated[sellerId] || []).filter(
      (it) => it.listing_id !== listingId
    );

    if (remaining.length === 0) {
      delete updated[sellerId];
    } else {
      updated[sellerId] = remaining;
    }

    setCartItems(updated);
    localStorage.setItem(getCartKey(), JSON.stringify(updated));
  };

  const calculateStoreTotal = (items) =>
    items.reduce((sum, item) => sum + item.card_listing.price * item.quantity, 0);

  const handleCheckout = (sellerId) => {
    if (!isAuthenticated) {
      /* CARRY THE DESTINATION, exactly as `ProtectedRoute` does. `/cart` is
         public and `/checkout` is not, so this page runs its own guard and
         never reaches that component — and it navigated bare, so login had
         nothing to explain itself with and returned the buyer to the
         marketplace instead of the cart they were paying for.

         No toast either. The login page now leads with "Sign in to check out"
         and a link back to this cart, so an error-styled toast saying the same
         thing is the second copy of one message — and this is not an error,
         it is a step.

         `from` is THIS page, not `/checkout`, and `reason` carries the intent
         instead. Checkout reads a per-seller handoff that only exists once the
         buyer has picked a seller here, so returning them straight to it would
         land on "There is nothing to check out" and bounce back — a flash of
         alarming copy, moments after signing in, on the way to where they were
         always going. */
      navigate('/login', {
        state: { from: { pathname: routes.CART }, reason: 'checkout' },
      });
      return;
    }

    if (!cartData[sellerId] || cartData[sellerId].length === 0) {
      showToast.error('No items available for checkout.');
      return;
    }

    const hasInvalidQuantity = cartData[sellerId].some((item) => {
      const availableStock = item.card_listing.quantity;
      if (item.quantity > availableStock) {
        showToast.error(
          `${item.card_listing.card?.name || 'Item'} only has ${availableStock} in stock, but you have ${item.quantity} in cart.`
        );
        return true;
      }
      return false;
    });

    if (hasInvalidQuantity) return;

    const orderItemsKey = user
      ? `orderItems_${user.id || user._id}`
      : 'orderItems_guest';

    const orderData = {
      sellerId,
      items: cartData[sellerId].map((it) => ({
        listing_id: it.card_listing.id,
        quantity: it.quantity,
      })),
    };

    localStorage.setItem(orderItemsKey, JSON.stringify(orderData));
    navigate('/checkout');
  };

  /* The view model. Prices are formatted HERE, once, so the pattern never
     formats currency and can never be handed anything but Rands. */
  const sellers = useMemo(
    () =>
      Object.keys(cartData).map((sellerId) => {
        const items = cartData[sellerId];
        const seller = items[0].card_listing?.seller;

        return {
          id: sellerId,
          storeName:
            seller?.store_name ||
            seller?.full_name ||
            seller?.first_name ||
            `Seller ${sellerId}`,
          storeHref: sellerId ? `/seller/${sellerId}` : undefined,
          rating:
            items[0].card_listing?.seller_rating_stats?.rating_out_of_5 ??
            (items[0].card_listing?.rating ? items[0].card_listing.rating / 20 : 0),
          joinedLabel: (() => {
            const joined = seller?.created_at || items[0].card_listing?.created_at;
            if (!joined) return undefined;
            const label = formatPrettyDate(joined);
            return label === 'N/A' ? undefined : `Joined ${label}`;
          })(),
          total: formatCurrency(calculateStoreTotal(items)),
          items: items.map((item) => {
            const listing = item.card_listing;
            const card = readCard(listing);
            return {
              id: listing.id,
              image: listing.images?.[0]?.image,
              set: card.setName,
              name: card.name,
              href: card.cardId
                ? `${routes.MARKETPLACE}/card/${card.cardId}/${listing.id}`
                : undefined,
              condition: listing.condition,
              /* Sent by `getListingsByIds` and dropped here until now. */
              finish: listing.finish,
              cardNumber: card.cardNumber,
              unitPrice: formatCurrency(listing.price),
              quantity: item.quantity,
              max: listing.quantity,
              lineTotal: formatCurrency(listing.price * item.quantity),
              note:
                listing.quantity > 0 && listing.quantity <= 2
                  ? `Only ${listing.quantity} left`
                  : undefined,
            };
          }),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cartData]
  );

  const itemCount = useMemo(
    () => Object.values(cartData).flat().length,
    [cartData]
  );

  const cartTotal = useMemo(
    () =>
      formatCurrency(
        Object.values(cartData).reduce(
          (sum, items) => sum + calculateStoreTotal(items),
          0
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cartData]
  );

  // The RAW (minimal, localStorage-backed) cart decides whether to show the
  // loading state, because the fresh `cartData` is empty until the listings
  // fetch resolves and that would otherwise read as "your cart is empty".
  const isRawCartEmpty = Object.keys(cartItems || {}).length === 0;

  return (
    <CartPageView
      /* Only promise a courier when we know one is coming. */
      shipsNationwide={fulfilmentMode === 'shipping'}
      sellers={sellers}
      itemCount={itemCount}
      cartTotal={cartTotal}
      loading={isLoading && !isRawCartEmpty}
      error={isError}
      onRetry={() => refetch()}
      onIncrease={handleIncrease}
      onDecrease={handleDecrease}
      onRemove={handleDelete}
      onCheckout={handleCheckout}
      skeletonCount={Object.values(cartItems || {}).flat().length || 3}
    />
  );
}

/* `formatPrettyDate` used to live here and was exported from this module. It
   formatted a seller's join date in the Asia/Karachi timezone on a South
   African marketplace, and nothing outside this directory ever imported it.
   B5's conversion dropped the join date along with the rest of
   `SellerSection`; it is restored above via the shared `formatPrettyDate` in
   `utils/common.js` (see that file for the Asia/Karachi FLAG, carried over
   verbatim). The seller's city stays gone — that removal was deliberate, per
   the standing product rule documented on `CartSellerGroup` in
   `design-system/patterns/cart/index.jsx`. */
